import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { ConsigneeValue } from "pages/shipper/RequestFormV2/defaults/consigneeDefaults";
import {
  getDefaultNotifyPartyValue,
  NotifyPartySyncOption,
} from "pages/shipper/RequestFormV2/defaults/notifyPartyDefaults";
import { ScheduleStopValue } from "pages/shipper/RequestFormV2/defaults/scheduleDefaults";
import { notifyPartyFieldPaths, getNotifyPartyFieldPath } from "@RequestFormV2/sections/Schedule/utils/notifyPartyFieldPaths";
import {
  DELIVERY_STOP_INDEX,
  mapConsigneeToNotifyParty,
  mapDeliveryStopToNotifyParty,
} from "@RequestFormV2/sections/Schedule/utils/mapToNotifyParty";
import { usePersistDraft } from "@RequestFormV2/hooks/usePersistDraft";

const SYNCED_OPTIONS: NotifyPartySyncOption[] = [
  "sameAsDeliveryAddressContact",
  "sameAsConsigneeAddressContact",
];

export function useNotifyPartySync() {
  const { getValues, setValue, watch } = useFormContext();
  const persistDraft = usePersistDraft();

  const sameAsAddressContact = watch(notifyPartyFieldPaths.same_as_address_contact) as NotifyPartySyncOption;
  const deliveryStop = watch(`schedule.stops.${DELIVERY_STOP_INDEX}`) as ScheduleStopValue | undefined;
  const consignee = watch("schedule.consignee") as ConsigneeValue | undefined;
  const deliveryStopKey = JSON.stringify(deliveryStop);
  const consigneeKey = JSON.stringify(consignee);

  const fieldsDisabled = SYNCED_OPTIONS.includes(sameAsAddressContact);

  const syncFromDelivery = useCallback(() => {
    const stop = getValues(`schedule.stops.${DELIVERY_STOP_INDEX}`) as ScheduleStopValue;
    if (!stop) return;

    const mapped = mapDeliveryStopToNotifyParty(stop);
    (Object.keys(mapped) as (keyof typeof mapped)[]).forEach((key) => {
      setValue(getNotifyPartyFieldPath(key), mapped[key]);
    });
  }, [getValues, setValue]);

  const syncFromConsignee = useCallback(() => {
    const consigneeValues = getValues("schedule.consignee") as ConsigneeValue;
    if (!consigneeValues) return;

    const mapped = mapConsigneeToNotifyParty(consigneeValues);
    (Object.keys(mapped) as (keyof typeof mapped)[]).forEach((key) => {
      setValue(getNotifyPartyFieldPath(key), mapped[key]);
    });
  }, [getValues, setValue]);

  const clearNotifyPartyFields = useCallback(() => {
    const defaults = getDefaultNotifyPartyValue();
    const fieldsToReset: (keyof typeof defaults)[] = [
      "address_reference_number",
      "address_id",
      "address_name",
      "address_street",
      "address_street_line2",
      "address_city",
      "address_state",
      "address_country",
      "address_zipcode",
      "airport",
      "airport_name",
      "seaport",
      "seaport_name",
      "contact_name",
      "contact_phone",
      "contact_email",
    ];
    fieldsToReset.forEach((field) => {
      setValue(notifyPartyFieldPaths[field], defaults[field]);
    });
    setValue(notifyPartyFieldPaths.form_address_contact_type, "custom");
  }, [setValue]);

  const handleSyncOptionChange = useCallback(
    (option: NotifyPartySyncOption) => {
      setValue(notifyPartyFieldPaths.same_as_address_contact, option);

      if (option === "sameAsDeliveryAddressContact") {
        syncFromDelivery();
      } else if (option === "sameAsConsigneeAddressContact") {
        syncFromConsignee();
      } else {
        clearNotifyPartyFields();
      }
      persistDraft("schedule");
    },
    [setValue, syncFromDelivery, syncFromConsignee, clearNotifyPartyFields, persistDraft],
  );

  useEffect(() => {
    if (sameAsAddressContact === "sameAsDeliveryAddressContact") {
      syncFromDelivery();
    }
  }, [sameAsAddressContact, deliveryStopKey, syncFromDelivery]);

  useEffect(() => {
    if (sameAsAddressContact === "sameAsConsigneeAddressContact") {
      syncFromConsignee();
    }
  }, [sameAsAddressContact, consigneeKey, syncFromConsignee]);

  return {
    sameAsAddressContact,
    fieldsDisabled,
    handleSyncOptionChange,
  };
}

export type NotifyPartySyncSlice = ReturnType<typeof useNotifyPartySync>;
